/**
 * Modern Vue 3 SPA Entry Point.
 * Clean architecture with permissions, i18n, and component library.
 */

import { createApp, type App as VueApp } from 'vue'
import { initErrorReporting } from './services/errorReporting'
import { createI18n, type I18n } from 'vue-i18n'
import App from './App.vue'

// Bootstrap the runtime contract registry so validate() can resolve any
// contract by name without per-composable side-effect imports.
import './contracts/registry-runtime'

import PrimeVue from 'primevue/config'
import { createFranchiseTheme } from './theme/preset'
import Tooltip from 'primevue/tooltip'
import { vExplain } from './explanations/vExplain'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import 'primeicons/primeicons.css'

import router from './router/index'

import { config } from './services/configService'
import { translationsService } from './services/translationsService'
import { permissionsService, permissionsState, type PermissionsData } from './services/permissionsService'
import { tokenService } from './services/tokenService'

import { eventBus } from './eventBus'
import { log, DebugLogger } from './utils/DebugLogger'

import 'vue-select/dist/vue-select.css'
import './theme/theme.css'

import { initTenant, tenantConfig } from './bootstrap/tenant'
import { initAuth } from './bootstrap/auth'
import { createPrimeVueLocale } from './bootstrap/primevue-locale'
import { numberFormats, datetimeFormats } from './bootstrap/i18n-formats'
import { registerGlobalComponents } from './bootstrap/global-components'
import { setupSentry } from './bootstrap/sentry-setup'

const showBootstrapError = (error: Error): void => {
  const errorDiv = document.createElement('div')
  errorDiv.innerHTML = `
    <div style="position: fixed; top: 20px; right: 20px; background: #f8d7da; color: #721c24; padding: 15px; border-radius: 5px; z-index: 10000; max-width: 400px;">
      <strong>Erro ao inicializar aplicação:</strong><br>
      ${error.message}<br>
      <small>Recarregue a página para tentar novamente.</small>
    </div>
  `
  document.body.appendChild(errorDiv)
}

interface TenantConfigService {
  isLoaded(): boolean
  get(key: string, fallback?: unknown): unknown
  getTenantInfo(): { id?: string | number; [key: string]: unknown }
}

const validateBeforeMount = (
  tenantConfigSvc: TenantConfigService,
  i18n: I18n,
  userLocale: string,
  messages: Record<string, Record<string, string>>
): string[] => {
  const validationErrors: string[] = []

  if (!tenantConfigSvc.isLoaded()) {
    validationErrors.push('Tenant config not loaded')
  }

  const i18nGlobal = i18n.global as unknown as { locale: { value: string } | string }
  const localeValue = typeof i18nGlobal.locale === 'string' ? i18nGlobal.locale : i18nGlobal.locale.value
  if (!localeValue) {
    validationErrors.push('i18n locale not set')
  }

  if (Object.keys(messages[userLocale] || {}).length === 0) {
    validationErrors.push('No translations loaded')
  }

  return validationErrors
}

interface AppWindow extends Window {
  VueApp?: VueApp
  permissions?: PermissionsData | null
  permissionsService?: typeof permissionsService
  permissionsState?: typeof permissionsState
  tokenService?: typeof tokenService
  translationsService?: typeof translationsService
  eventBus?: typeof eventBus
  DebugLogger?: typeof DebugLogger
}

const setupWindowRefs = (app: VueApp, permissions: PermissionsData | null): void => {
  if (typeof window === 'undefined') return

  const w = window as AppWindow
  w.VueApp = app
  w.permissions = permissions
  w.permissionsService = permissionsService
  w.permissionsState = permissionsState
  w.tokenService = tokenService
  w.translationsService = translationsService
  w.eventBus = eventBus
  w.DebugLogger = DebugLogger
  window.dispatchEvent(new CustomEvent('vue-app-ready'))
}

async function bootstrapApp(): Promise<void> {
  try {
    log('App', 'bootstrap', '🚀 Bootstrapping Vue application...')

    await initTenant()

    const permissions = await initAuth()

    if (permissions === undefined) {
      return
    }

    let userLocale =
      permissions?.user?.language || permissions?.locale || (config.app as { defaultLocale: string }).defaultLocale
    // Rotas públicas (sem sessão) podem carregar o idioma via ?lang — ex.: a
    // página de pré-cadastro linka os termos/política com o idioma do remetente,
    // e essas páginas abrem em nova aba (bootstrap novo, sem herdar o locale).
    if (!permissions) {
      // O link do laudo leva o idioma de quem o gerou como o Cognito o guarda
      // ('es-EC', ou o legado 'ES'); passa pela mesma normalização do app.
      const rawLang = new URLSearchParams(window.location.search).get('lang')
      const qLang = rawLang ? permissionsService.normalizeLocale(rawLang) : null
      if (qLang && translationsService.supportedLocales.some((l) => qLang.startsWith(l))) {
        userLocale = qLang
      }
    }
    log('App', 'bootstrap', `Fetching translations for locale: ${userLocale}`, {
      userLanguage: permissions?.user?.language,
      permissionsLocale: permissions?.locale,
      resolved: userLocale
    })

    const messages = await translationsService.initialize([userLocale], ['Frontend'])
    log('App', 'bootstrap', '✅ Translations loaded:', {
      locale: userLocale,
      translationCount: Object.keys(messages[userLocale] || {}).length
    })

    /**
     * A key vue-i18n could not find.
     *
     * Registers it with translations-service (unchanged), AND teaches the local
     * catalog immediately.
     *
     * WHY THE SECOND HALF MATTERS. When `missing` returns a string, vue-i18n
     * uses it VERBATIM — it does not compile or interpolate it. So a key
     * carrying placeholders, t('{n} dia', n) or t('Máximo: {valor}', {...}),
     * renders the literal "{n} dia" until the key exists in the catalog.
     * `formatFallbackMessages: true` does not rescue it, and neither does
     * returning undefined instead of the key — both were measured, both render
     * the literal. This is a property of vue-i18n's fallback path, not a
     * misconfiguration.
     *
     * Merging the key means the NEXT render resolves it and interpolates
     * correctly, instead of waiting for translations-service to persist it and
     * the app to reload with a fresh catalog. It shrinks the broken window from
     * "until the next page load" to "one render".
     *
     * It does NOT fix the first render — nothing can, short of the key already
     * being in the catalog. Placeholders in t() keys therefore remain a known
     * hazard: ~29 call sites still carry them, and each renders its literal
     * once per environment before healing.
     */
    const handleMissingTranslation = (locale: string, key: string): string => {
      const defaultValue = String(key)
      translationsService.addMissingTranslation('Frontend', key, defaultValue, locale).catch(() => {})

      try {
        i18n.global.mergeLocaleMessage(locale, { [key]: defaultValue })
      } catch {
        // Never let catalog self-healing break a render.
      }

      return defaultValue
    }

    const i18n = createI18n({
      locale: userLocale,
      fallbackLocale: 'pt-BR',
      messages,
      numberFormats,
      datetimeFormats,
      legacy: false,
      globalInjection: true,
      warnHtmlMessage: false,
      formatFallbackMessages: true,
      missingWarn: false,
      fallbackWarn: false,
      missing: handleMissingTranslation
    })
    const i18nGlobal = i18n.global as unknown as { locale: { value: string } | string }
    const localeValue = typeof i18nGlobal.locale === 'string' ? i18nGlobal.locale : i18nGlobal.locale.value
    log('App', 'bootstrap', '✅ i18n configured with locale:', localeValue)

    // Skip tutorial init on public routes (no permissions) to avoid 401 redirect loop on /login.
    if (permissions) {
      const { tutorialService } = await import('./services/tutorialService')
      await tutorialService.init(i18n)

      const { registerAllTutorials } = await import('./domains/tutorials/definitions/index')
      registerAllTutorials()
      log('App', 'bootstrap', '✅ Tutorial definitions registered')
    }

    const app = createApp(App)
    initErrorReporting(app)
    log('App', 'bootstrap', '✅ Vue app created')

    app.use(i18n)

    const i18nT = (i18n.global as unknown as { t: (key: string) => string }).t
    const primeVueLocale = createPrimeVueLocale(i18nT)

    document.documentElement.offsetHeight

    const franchiseTheme = createFranchiseTheme(tenantConfig)
    log('App', 'bootstrap', '🎨 Created dynamic theme with tenant colors:', {
      primaryColor: tenantConfig.get('primary_color') || 'default',
      secondaryColor: tenantConfig.get('secondary_color') || 'default',
      cssVarsPrimary: getComputedStyle(document.documentElement).getPropertyValue('--tenant-primary-color').trim(),
      cssVarsSecondary: getComputedStyle(document.documentElement).getPropertyValue('--tenant-secondary-color').trim()
    })

    setupSentry(app, permissions, eventBus)

    const primeVueTheme = { preset: franchiseTheme, options: { darkModeSelector: false } }
    app.use(PrimeVue, { theme: primeVueTheme, locale: primeVueLocale })
    app.use(ToastService)
    app.use(ConfirmationService)
    app.directive('tooltip', Tooltip)
    app.directive('explain', vExplain)
    app.use(router)
    router.afterEach((to) => {
      eventBus.emit('route.ready', { path: to.path })
    })
    log(
      'App',
      'bootstrap',
      '✅ Plugins installed (i18n, PrimeVue with Dynamic Theme, ToastService, ConfirmationService, Router, Tooltip, Explain)'
    )

    app.provide('permissions', permissions)
    app.provide('permissionsService', permissionsService)
    app.provide('translationsService', translationsService)
    log('App', 'bootstrap', '✅ Services provided globally')

    registerGlobalComponents(app)

    const { default: SOReportAttributesList } = await import('./domains/so-orders/SOReportAttributesList.vue')
    app.component('SOReportAttributesList', SOReportAttributesList)

    app.config.globalProperties.$debug = DebugLogger

    const validationErrors = validateBeforeMount(
      tenantConfig as unknown as TenantConfigService,
      i18n,
      userLocale,
      messages
    )

    if (validationErrors.length > 0) {
      log('App', 'bootstrap', '⚠️ Validation warnings before mount:', validationErrors)
    }

    log('App', 'bootstrap', '🚀 Mounting Vue app to DOM...')
    app.mount('#main-app')
    log('App', 'bootstrap', '✅ Application mounted successfully!', {
      locale: localeValue,
      tenantId: tenantConfig.getTenantInfo().id,
      primaryColor: tenantConfig.get('primary_color'),
      translationsLoaded: Object.keys(messages[userLocale] || {}).length
    })

    eventBus.emit('app.ready', { app })

    setupWindowRefs(app, permissions)
  } catch (err) {
    console.error('❌ Failed to bootstrap application:', err)
    showBootstrapError(err as Error)
    throw err
  }
}

bootstrapApp()
